/**
 * MOT DOI TUONG TAI LIEU CHO MOI NGUON - khung xem khong duoc gia dinh chi co mot.
 *
 * Sau khi ghep tep, so do trang chua nhung o tro sang tai lieu KHAC. Cho nay
 * giu mot tai lieu pdf.js dang mo cho tung ma nguon xuat hien trong so do, va
 * tra ve ban do "ma nguon -> tai lieu + sieu du lieu trang" ma tang ve doc.
 *
 * BA QUYET DINH VE BO NHO
 * -----------------------
 *  1. Chi mo nhung nguon THUC SU co trong so do dang co hieu luc. Ghi nhan mot
 *     tep de ghep roi hoan tac ngay thi khong mo gi ca.
 *
 *  2. KHONG dong mot nguon ngay khi no roi khoi so do. Ctrl+Z sau khi xoa trang
 *     ghep se mang no quay lai trong vai chuc mili giay, va dong/mo lai mot tai
 *     lieu pdf.js la dung ca mot worker - dat hon nhieu so voi phan bo nho tiet
 *     kiem duoc. Toan bo duoc dong khi doi tep (App goi closeSourceViews).
 *
 *  3. Sieu du lieu trang duoc lay MOT LAN luc mo. No khong doi, va hoi lai o
 *     moi lan ve la ep tang tren tinh lai bo cuc lien tuc.
 */
import { useEffect, useMemo, useState } from 'react';
import type { DocumentId, DocumentInfo, PdfCore } from '@pdfeditor/types';

import { ORIGINAL_DOC_ID, foreignDocIds, type PagePlan } from '../core/pages/plan';
import { asSourceViewCore } from '../core/pages/capability';
import type { PlanDocumentMeta, SourcePageMeta } from './planView';

export interface PlanDocumentEntry extends PlanDocumentMeta {
  /** Tai lieu pdf.js de lay doi tuong trang. */
  doc: DocumentId;
  pages: readonly SourcePageMeta[];
}

export type PlanDocuments = ReadonlyMap<string, PlanDocumentEntry>;

function metaOf(info: DocumentInfo): SourcePageMeta[] {
  return info.pages.map((p) => ({
    widthPt: p.widthPt,
    heightPt: p.heightPt,
    rotation: p.rotation,
    hasTextLayer: p.hasTextLayer,
  }));
}

export function usePlanDocuments(
  core: PdfCore | null,
  mainDoc: DocumentId | null,
  mainInfo: DocumentInfo | null,
  plan: PagePlan,
): PlanDocuments {
  const [foreign, setForeign] = useState<ReadonlyMap<string, PlanDocumentEntry>>(new Map());

  // Doi tep thi bo het ban do cu NGAY, khong doi lan mo sau tra ve: giu lai mot
  // muc cua tep truoc la de khung xem ve trang cua tai lieu da dong.
  useEffect(() => {
    setForeign(new Map());
  }, [mainDoc?.id]);

  // Danh sach ma nguon la mot chuoi de so sanh duoc theo gia tri: so do doi
  // tham chieu sau MOI lan bam, con tap ma nguon thi hiem khi doi.
  const neededKey = useMemo(() => foreignDocIds(plan).sort().join('|'), [plan]);

  useEffect(() => {
    const source = asSourceViewCore(core);
    if (!source) return;
    const needed = neededKey.length > 0 ? neededKey.split('|') : [];
    const missing = needed.filter((id) => !foreign.has(id));
    if (missing.length === 0) return;

    let cancelled = false;
    void (async () => {
      const opened: [string, PlanDocumentEntry][] = [];
      for (const docId of missing) {
        const result = await source.openSourceForView(docId);
        if (cancelled) return;
        // Mot nguon khong mo duoc thi o trang cua no o lai trang thai "dang
        // cho" va hien o giu cho co nhan. KHONG duoc lui ve trang lang gieng.
        if (!result.ok) continue;
        opened.push([docId, { doc: result.value.doc, pages: metaOf(result.value.info) }]);
      }
      if (cancelled || opened.length === 0) return;
      setForeign((prev) => {
        const next = new Map(prev);
        for (const [id, entry] of opened) next.set(id, entry);
        return next;
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [core, neededKey, foreign]);

  return useMemo(() => {
    const map = new Map<string, PlanDocumentEntry>(foreign);
    if (mainDoc && mainInfo) {
      map.set(ORIGINAL_DOC_ID, { doc: mainDoc, pages: metaOf(mainInfo) });
    }
    return map;
  }, [foreign, mainDoc, mainInfo]);
}
